import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import ArchiveIcon from "@material-ui/icons/Archive";
import UnarchiveIcon from "@material-ui/icons/Unarchive";
import DeleteIcon from "@material-ui/icons/Delete";

import ListPagination from "../../../containers/ListPagination";
import ConfirmAction from "../../Tools/ConfirmAction";
import {
  classificationLabel,
  classificationParams,
  CLASSIFICATION_OPTIONS,
  GENDER_OPTIONS,
  rulesApi,
  SET_HEIGHT_OPTIONS,
  setHeightLabel,
} from "../../../utils/rulesApi";
import {
  AuditStamp,
  Breadcrumbs,
  rulesPath,
  sortDirection,
  StatusChip,
  useDebouncedCommit,
} from "../common";
import { useRuleStyles } from "../styles";
import {
  keepState,
  resolveReturnTo,
  returnState,
} from "../../../utils/returnTo";
import CopySetDialog from "./CopySetDialog";

const DEFAULT_SIZE = 50;

const SORTABLE = [
  { key: "AgeGroup", label: "Age" },
  { key: "Gender", label: "Gender" },
  { key: "Height", label: "Height" },
  { key: "RuleName", label: "Rule set name" },
  { key: "JumpSchema", label: "Schema" },
];

const readQuery = (search) => {
  const sp = new URLSearchParams(search);
  return {
    query: sp.get("query") || "",
    ageGroup: sp.get("ageGroup") || "",
    gender: sp.get("gender") || "",
    height: sp.get("height") || "",
    classification: sp.get("classification") || "",
    status: sp.get("status") || "ACTIVE",
    sort: sp.get("sort") || "",
    isAscending: sp.get("isAscending") !== "false",
    page: Number(sp.get("page")) || 0,
    size: Number(sp.get("size")) || DEFAULT_SIZE,
  };
};

const writeQuery = (state) => {
  const sp = new URLSearchParams();
  ["query", "ageGroup", "gender", "height", "classification", "sort"].forEach(
    (key) => {
      if (state[key]) sp.set(key, state[key]);
    },
  );
  if (state.status !== "ACTIVE") sp.set("status", state.status);
  if (!state.isAscending) sp.set("isAscending", "false");
  if (state.page) sp.set("page", String(state.page));
  if (state.size !== DEFAULT_SIZE) sp.set("size", String(state.size));
  const qs = sp.toString();
  return qs ? `?${qs}` : "";
};

const EMPTY_PAGE = { items: [], totalElements: 0, currentPage: 0 };

/** Screen 2: the rule sets of one group. */
const RuleSets = () => {
  const classes = useRuleStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { groupId } = useParams();

  const [group, setGroup] = useState(null);
  const [state, setState] = useState(() => readQuery(location.search));
  const [searchDraft, setSearchDraft] = useState(state.query);
  const [ageDraft, setAgeDraft] = useState(state.ageGroup);
  const [result, setResult] = useState(EMPTY_PAGE);
  const [loading, setLoading] = useState(false);
  const [copying, setCopying] = useState(null);
  const [confirm, setConfirm] = useState(null); // { kind: 'status' | 'delete', set }
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    rulesApi
      .group(groupId)
      .then(setGroup)
      .catch((e) => toast.error(e.message));
  }, [groupId]);

  useDebouncedCommit(searchDraft, (query) =>
    setState((prev) =>
      prev.query === query ? prev : { ...prev, query, page: 0 },
    ),
  );
  useDebouncedCommit(ageDraft, (ageGroup) =>
    setState((prev) =>
      prev.ageGroup === ageGroup ? prev : { ...prev, ageGroup, page: 0 },
    ),
  );

  const load = useCallback(() => {
    setLoading(true);
    rulesApi
      .setsInGroup(groupId, {
        page: state.page,
        size: state.size,
        sort: state.sort || undefined,
        isAscending: state.isAscending,
        ageGroup: state.ageGroup || undefined,
        gender: state.gender || undefined,
        height: state.height || undefined,
        status: state.status,
        query: state.query || undefined,
        ...classificationParams(state.classification),
      })
      .then((data) => setResult(data || EMPTY_PAGE))
      .catch((e) => toast.error(e.message))
      .finally(() => setLoading(false));
  }, [groupId, state]);

  useEffect(() => {
    load();
    const nextSearch = writeQuery(state);
    if (nextSearch !== location.search) {
      navigate(
        { pathname: location.pathname, search: nextSearch },
        { replace: true, state: keepState(location) },
      );
    }
    // location is intentionally not a dependency: this effect writes it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, groupId]);

  const openSet = (id) =>
    navigate(rulesPath.set(id), { state: returnState(location) });

  const patch = (changes) =>
    setState((prev) => ({ ...prev, page: 0, ...changes }));

  const onSort = (key) =>
    setState((prev) => ({
      ...prev,
      page: 0,
      sort: key,
      isAscending: prev.sort === key ? !prev.isAscending : true,
    }));

  const runConfirm = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      if (confirm.kind === "delete") {
        await rulesApi.deleteSet(confirm.set.id);
        toast.success(`Rule set ${confirm.set.ruleName} deleted`);
      } else {
        const next =
          confirm.set.status === "DEPRECATED" ? "ACTIVE" : "DEPRECATED";
        await rulesApi.setStatus(confirm.set.id, next);
        toast.success(
          next === "DEPRECATED"
            ? `${confirm.set.ruleName} is deprecated and hidden from meet setup`
            : `${confirm.set.ruleName} is active again`,
        );
      }
      setConfirm(null);
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const rows = result.items || [];
  const groupName = group?.name || `Group ${groupId}`;
  const showStatus = state.status !== "ACTIVE";

  return (
    <Box className={classes.page}>
      <Container maxWidth="xl">
        <Breadcrumbs
          items={[
            { label: "Home", to: "/" },
            {
              label: "Rules",
              to: resolveReturnTo(location, rulesPath.groups()),
            },
            { label: groupName },
          ]}
        />
        <Box className={classes.header}>
          <Box>
            <Box className={classes.titleRow}>
              <Typography component="h1" className={classes.title}>
                Rule sets · {groupName}
              </Typography>
              {group && <StatusChip status={group.status} />}
            </Box>
            <Typography className={classes.subtitle}>
              Meet setup offers a set to an event only when age group, gender,
              height and the three classification flags all match exactly.
              Deprecated sets stay with their events but are no longer offered.
            </Typography>
          </Box>
          <Tooltip
            title={
              group?.status === "DEPRECATED"
                ? "This group is deprecated; reactivate it before adding sets."
                : ""
            }
          >
            <span>
              <Button
                variant="contained"
                color="primary"
                className={classes.touch}
                disabled={group?.status === "DEPRECATED"}
                onClick={() =>
                  navigate(rulesPath.newSet(groupId), {
                    state: returnState(location),
                  })
                }
              >
                New rule set
              </Button>
            </span>
          </Tooltip>
        </Box>

        <Paper
          className={classes.filters}
          component="section"
          aria-label="Filters"
        >
          <TextField
            label="Search"
            placeholder="Name, code or id"
            variant="outlined"
            size="small"
            className={classes.filterFieldWide}
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            inputProps={{ "aria-label": "Search rule sets" }}
          />
          <TextField
            label="Age group"
            placeholder="e.g. 14-15"
            variant="outlined"
            size="small"
            className={classes.filterFieldNarrow}
            value={ageDraft}
            onChange={(e) => setAgeDraft(e.target.value)}
          />
          <TextField
            select
            label="Gender"
            variant="outlined"
            size="small"
            className={classes.filterFieldNarrow}
            value={state.gender}
            onChange={(e) => patch({ gender: e.target.value })}
          >
            <MenuItem value="">All</MenuItem>
            {GENDER_OPTIONS.map((g) => (
              <MenuItem key={g} value={g}>
                {g}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Height"
            variant="outlined"
            size="small"
            className={classes.filterFieldNarrow}
            value={state.height}
            onChange={(e) => patch({ height: e.target.value })}
          >
            <MenuItem value="">All</MenuItem>
            {SET_HEIGHT_OPTIONS.map((h) => (
              <MenuItem key={h.value} value={h.value}>
                {h.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Classification"
            variant="outlined"
            size="small"
            className={classes.filterFieldNarrow}
            value={state.classification}
            onChange={(e) => patch({ classification: e.target.value })}
          >
            <MenuItem value="">All</MenuItem>
            {CLASSIFICATION_OPTIONS.map((c) => (
              <MenuItem key={c.value} value={c.value}>
                {c.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Status"
            variant="outlined"
            size="small"
            className={classes.filterFieldNarrow}
            value={state.status}
            onChange={(e) => patch({ status: e.target.value })}
          >
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="DEPRECATED">Deprecated</MenuItem>
            <MenuItem value="ALL">All</MenuItem>
          </TextField>
        </Paper>

        <TableContainer component={Paper}>
          <Table
            size="small"
            aria-label={`Rule sets of ${groupName}`}
            aria-busy={loading}
          >
            <TableHead>
              <TableRow>
                {SORTABLE.map((col) => (
                  <TableCell
                    key={col.key}
                    scope="col"
                    className={classes.headerCell}
                    sortDirection={sortDirection(state, col.key)}
                  >
                    <TableSortLabel
                      active={state.sort === col.key}
                      direction={sortDirection(state, col.key) || "asc"}
                      onClick={() => onSort(col.key)}
                    >
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell scope="col" className={classes.headerCell}>
                  Classification
                </TableCell>
                <TableCell
                  scope="col"
                  className={classes.headerCell}
                  sortDirection={sortDirection(state, "Code")}
                >
                  <TableSortLabel
                    active={state.sort === "Code"}
                    direction={sortDirection(state, "Code") || "asc"}
                    onClick={() => onSort("Code")}
                  >
                    Code
                  </TableSortLabel>
                </TableCell>
                <TableCell scope="col" className={classes.headerCell}>
                  Rules
                </TableCell>
                <TableCell scope="col" className={classes.headerCell}>
                  Events
                </TableCell>
                {showStatus && (
                  <TableCell scope="col" className={classes.headerCell}>
                    Status
                  </TableCell>
                )}
                <TableCell
                  scope="col"
                  className={classes.headerCell}
                  sortDirection={sortDirection(state, "UpdatedAt")}
                >
                  <TableSortLabel
                    active={state.sort === "UpdatedAt"}
                    direction={sortDirection(state, "UpdatedAt") || "asc"}
                    onClick={() => onSort("UpdatedAt")}
                  >
                    Updated
                  </TableSortLabel>
                </TableCell>
                <TableCell scope="col" className={classes.headerCell}>
                  <span className="sr-only">Actions</span>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={12}>
                    <Box className={classes.progressWrap}>
                      <CircularProgress aria-label="Loading rule sets" />
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={12} className={classes.empty}>
                    No rule sets match these filters.
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                rows.map((row) => {
                  const deprecated = row.status === "DEPRECATED";
                  const deletable = row.eventCount === 0;
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell className={classes.cell}>
                        {row.ageGroup}
                      </TableCell>
                      <TableCell className={classes.cell}>
                        {row.gender}
                      </TableCell>
                      <TableCell className={classes.cell}>
                        {setHeightLabel(row.height)}
                      </TableCell>
                      <TableCell className={classes.cell}>
                        <Button
                          color="primary"
                          className={classes.touch}
                          style={{
                            textTransform: "none",
                            justifyContent: "flex-start",
                          }}
                          onClick={() => openSet(row.id)}
                          aria-label={`Open rule set ${row.ruleName}`}
                        >
                          {row.ruleName}
                        </Button>
                      </TableCell>
                      <TableCell className={`${classes.cell} ${classes.mono}`}>
                        {row.jumpSchema}
                      </TableCell>
                      <TableCell className={classes.cell}>
                        <Chip
                          size="small"
                          label={classificationLabel(row)}
                          className={classes.chipNeutral}
                        />
                      </TableCell>
                      <TableCell className={`${classes.cell} ${classes.mono}`}>
                        {row.code}
                      </TableCell>
                      <TableCell
                        className={`${classes.cell} ${classes.number}`}
                      >
                        {row.ruleCount}
                      </TableCell>
                      <TableCell
                        className={`${classes.cell} ${classes.number}`}
                      >
                        {row.eventCount}
                      </TableCell>
                      {showStatus && (
                        <TableCell className={classes.cell}>
                          <StatusChip status={row.status} />
                        </TableCell>
                      )}
                      <TableCell className={classes.cell}>
                        <AuditStamp audit={row.audit} />
                      </TableCell>
                      <TableCell className={classes.cell}>
                        <Box className={classes.actions}>
                          <IconButton
                            className={classes.iconButton}
                            aria-label={`Edit ${row.ruleName}`}
                            onClick={() => openSet(row.id)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            className={classes.iconButton}
                            aria-label={`Copy ${row.ruleName}`}
                            onClick={() => setCopying(row)}
                          >
                            <FileCopyIcon />
                          </IconButton>
                          <IconButton
                            className={classes.iconButton}
                            aria-label={
                              deprecated
                                ? `Reactivate ${row.ruleName}`
                                : `Deprecate ${row.ruleName}`
                            }
                            onClick={() =>
                              setConfirm({ kind: "status", set: row })
                            }
                          >
                            {deprecated ? <UnarchiveIcon /> : <ArchiveIcon />}
                          </IconButton>
                          <Tooltip
                            title={
                              deletable
                                ? ""
                                : `Used by ${row.eventCount} event${
                                    row.eventCount === 1 ? "" : "s"
                                  }; deprecate it instead.`
                            }
                          >
                            <span>
                              <IconButton
                                className={classes.iconButton}
                                aria-label={`Delete ${row.ruleName}`}
                                disabled={!deletable}
                                onClick={() =>
                                  setConfirm({ kind: "delete", set: row })
                                }
                              >
                                <DeleteIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
          <ListPagination
            filters={{
              page: result.currentPage,
              limit: state.size,
              total: result.totalElements,
            }}
            fetchFunction={({ page, limit }) =>
              setState((prev) => ({
                ...prev,
                page,
                size: Number(limit) || DEFAULT_SIZE,
              }))
            }
          />
        </TableContainer>
      </Container>

      <CopySetDialog
        open={Boolean(copying)}
        set={copying}
        onClose={() => setCopying(null)}
      />

      <ConfirmAction
        open={Boolean(confirm)}
        title={
          confirm?.kind === "delete"
            ? "Delete this rule set?"
            : confirm?.set?.status === "DEPRECATED"
            ? "Reactivate this rule set?"
            : "Deprecate this rule set?"
        }
        text={
          confirm?.kind === "delete"
            ? `${
                confirm?.set?.ruleName ?? ""
              } is used by no event and will be removed together with its links. The rules themselves are kept.`
            : confirm?.set?.status === "DEPRECATED"
            ? `${
                confirm?.set?.ruleName ?? ""
              } will be offered in meet setup again.`
            : `${
                confirm?.set?.ruleName ?? ""
              } will no longer be offered in meet setup. Events already using it keep it.`
        }
        confirmLabel={
          confirm?.kind === "delete"
            ? "Delete"
            : confirm?.set?.status === "DEPRECATED"
            ? "Reactivate"
            : "Deprecate"
        }
        busy={busy}
        onConfirm={runConfirm}
        onCancel={() => setConfirm(null)}
      />
    </Box>
  );
};

export default RuleSets;
